import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Columns3, ArrowLeft, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function CalendarColumnsSettingsPage() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);   // [{key,label,visible}]
  const [brandFamily, setBrandFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragKey, setDragKey] = useState(null);

  const rowRefs = useRef({});       // key -> DOM node
  const draggingRef = useRef(null); // key currently dragged

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/column-order`, authHeader());
      setColumns((r.data?.columns || []).map((c) => ({ ...c, visible: c.visible !== false })));
      setBrandFamily(r.data?.brand_family || null);
    } catch {
      toast.error('Kalender-Spalten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const moveBefore = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setColumns((prev) => {
      const fromIdx = prev.findIndex((c) => c.key === fromKey);
      const toIdx = prev.findIndex((c) => c.key === toKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  // Pointer-basiertes Umsortieren (funktioniert mit Maus UND Touch/iPad).
  const onPointerDown = (e, key) => {
    e.preventDefault();
    draggingRef.current = key;
    setDragKey(key);
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  };

  const onPointerMove = (e) => {
    const key = draggingRef.current;
    if (!key) return;
    const y = e.clientY;
    // Zeile unter dem Zeiger finden und davor/dahinter einsortieren.
    for (const [k, node] of Object.entries(rowRefs.current)) {
      if (!node || k === key) continue;
      const r = node.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom) {
        moveBefore(key, k);
        break;
      }
    }
  };

  const endDrag = () => { draggingRef.current = null; setDragKey(null); };

  const toggleVisible = (key) => {
    setColumns((prev) => prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  };

  const save = async () => {
    const visibleCount = columns.filter((c) => c.visible).length;
    if (visibleCount === 0) {
      toast.error('Mindestens eine Spalte muss sichtbar bleiben');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/calendar/column-order`, {
        order: columns.map((c) => c.key),
        hidden: columns.filter((c) => !c.visible).map((c) => c.key),
      }, authHeader());
      toast.success('Gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6" data-testid="calendar-columns-page">
      <button onClick={() => navigate('/settings')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="calendar-columns-back">
        <ArrowLeft className="h-4 w-4" /> Zurück zu Einstellungen
      </button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Columns3 className="h-5 w-5 text-purple-600" /> Kalenderspalten</CardTitle>
          <CardDescription>
            Ziehe die Spalten in die gewünschte Reihenfolge und blende einzelne Spalten aus. Die verfügbaren Spalten
            richten sich nach deiner Branche{brandFamily ? ` (${brandFamily})` : ''}. Die Einstellung gilt für den Kalender in der App.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Lade…</p>
          ) : columns.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">Keine Spalten für diese Branche.</p>
          ) : (
            <>
              <div className="space-y-2" data-testid="calendar-columns-list">
                {columns.map((col, i) => (
                  <div
                    key={col.key}
                    ref={(n) => { rowRefs.current[col.key] = n; }}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 transition-colors select-none
                      ${dragKey === col.key ? 'border-purple-400 bg-purple-50 shadow' : 'bg-white'}
                      ${!col.visible ? 'opacity-70' : ''}`}
                    data-testid={`calendar-column-${col.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        aria-label="Zum Umsortieren ziehen"
                        className="touch-none cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 -ml-1"
                        style={{ touchAction: 'none' }}
                        onPointerDown={(e) => onPointerDown(e, col.key)}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        data-testid={`calendar-column-drag-${col.key}`}
                      >
                        <GripVertical className="h-5 w-5" />
                      </button>
                      <span className="text-xs font-mono text-gray-400 w-5">{i + 1}.</span>
                      <span className={`font-medium ${col.visible ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{col.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {col.visible
                        ? <Eye className="h-4 w-4 text-emerald-500" />
                        : <EyeOff className="h-4 w-4 text-gray-400" />}
                      <Switch
                        checked={col.visible}
                        onCheckedChange={() => toggleVisible(col.key)}
                        data-testid={`calendar-column-visible-${col.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">Tipp: Am Griff ziehen zum Umsortieren (auch per Touch) · Schalter blendet eine Spalte aus.</p>
              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="calendar-columns-save">
                  {saving ? 'Speichern…' : 'Speichern'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
